import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到 - 白洁传研究中心" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-16 px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">分类未找到</h1>
              <p className="mt-3 text-foreground-600">请返回首页浏览其他分类</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `白洁传${category.name} - 白洁传研究中心`,
    description: category.description,
    url: `https://baijiezhuan.com/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://baijiezhuan.com/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`白洁传${category.name} - 白洁传研究中心`}
        description={category.description}
        keywords={`白洁传${category.name},白洁传,${category.name}`}
        canonicalPath={`/category/${category.slug}`}
        schemaData={webPageSchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: category.name },
                ]}
              />
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-foreground-600 text-sm">
                    {category.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="mb-10 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max pb-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-xl"></i>
                </div>
                <p className="mt-4 text-foreground-600">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}